<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';

import { imgProps } from '#image/components/_base';
import { useColorMode, useImage } from '#imports';
import useIsHuman from '~/composables/useIsHuman';

type ImageType = 'big' | 'default';
type ColorMode = 'dark-mode' | 'light-mode';
type PlaceholderType = 'skeleton' | 'missing' | 'missingBig';
type Placeholders = Record<ColorMode, Record<PlaceholderType, string>>;

defineOptions({
    inheritAttrs: false
});

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    mode: {
        type: String as () => ColorMode,
        required: false
    },
    type: {
        type: String as () => ImageType,
        default: 'default'
    },
    ...imgProps,
    animatedSrc: {
        type: String,
        default: ''
    },
    preloadMargin: {
        type: Number,
        default: 0
    },
    width: {
        type: Number,
        required: false
    },
    height: {
        type: Number,
        required: false
    }
});

const isError = ref(false);
const isLoaded = ref(false);
const isLoading = ref(true);
let fallbackTimer: NodeJS.Timeout | null = null;

const currentSrc = ref(props.src);
const target = useTemplateRef<HTMLDivElement>('target');
const targetIsVisible = shallowRef(false);
const allResourcesLoaded = ref(false);
const isAnimated = ref(false);

const intersectionObserver = useIntersectionObserver(
    target,
    ([entry]) => {
        targetIsVisible.value = entry?.isIntersecting || false;
    },
    { rootMargin: `${props.preloadMargin || `-${props.height || 200}`}px` }

);

const img = useImage();
const attrs = useAttrs();
const colorMode = useColorMode();
const isHuman = useIsHuman();

const placeholders: Placeholders = {
    'dark-mode': {
        skeleton: img('/skeleton-dark.svg'),
        missing: img('/missing-dark.svg'),
        missingBig: img('/missing-small-dark.svg')
    },
    'light-mode': {
        skeleton: img('/skeleton-light.svg'),
        missing: img('/missing-light.svg'),
        missingBig: img('/missing-small-light.svg')
    }
};

const placeholder = computed(() => {
    const mode = props.mode || colorMode.value as ColorMode;

    if (isError.value) {
        return props.type === 'big'
            ? placeholders[mode].missingBig
            : placeholders[mode].missing;
    }

    return placeholders[mode].skeleton;
});

function handleError() {
    isLoading.value = false;
    isError.value = true;
}

function handleLoad() {
    isLoaded.value = true;
    isLoading.value = false;
    isError.value = false;
}

function handleLoading() {
    isLoaded.value = false;
    isLoading.value = true;
    isError.value = false;
}

function calcSizes({ width, height }: { width?: number; height?: number }) {
    return {
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined
    };
}

function cleanAttrs(attrs: Record<string, unknown>) {
    const { srcset, sizes, width, height, ...cleanedAttrs } = attrs;
    return cleanedAttrs;
}

watch(() => currentSrc.value, (newSrc, oldSrc) => {
    if (newSrc && !oldSrc) handleLoading();
    if (!newSrc || newSrc.trim() === '') handleError();
});

watch(() => [
    allResourcesLoaded.value,
    targetIsVisible.value,
    isLoaded.value,
    isLoading.value,
    isHuman.value

], async (newValue) => {
    const [allResourcesLoaded, targetIsVisible, isLoaded, isLoading, isHuman]
        = newValue;
    if (
        allResourcesLoaded
        && targetIsVisible
        && !isLoading
        && isLoaded
        && isHuman
        && props.animatedSrc
    ) {
        await nextTick();
        queueMicrotask(() => {
            currentSrc.value = props.animatedSrc;
            isAnimated.value = true;
            if (intersectionObserver) intersectionObserver.stop();
        });
    }
});

const setResourcesLoaded = () => {
    if (!allResourcesLoaded.value) allResourcesLoaded.value = true;
};

const handleWindowLoad = () => {
    setResourcesLoaded();
    window.removeEventListener('load', handleWindowLoad);
};

onMounted(() => {
    if (!currentSrc.value || currentSrc.value.trim() === '') {
        handleError();
        return;
    }

    if (document.readyState === 'complete') setResourcesLoaded();
    else if (window) window.addEventListener('load', handleWindowLoad);

    fallbackTimer = setTimeout(() => {
        setResourcesLoaded();
    }, 7000);
});

onUnmounted(() => {
    if (intersectionObserver) intersectionObserver.stop();
    window.removeEventListener('load', handleWindowLoad);

    if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
    }
});

</script>

<template>
  <NuxtImg v-bind="{ ...attrs, ...props }"
           :custom="true"
           :key="props.src"
           :alt="props.alt"
           :width="props.width"
           :height="props.height"
           :style="calcSizes({ width: props.width, height: props.height })"
           v-slot="{ isLoaded: slotLoaded, imgAttrs: slotAttrs }"
  >
    <img v-if="slotLoaded && !isError"
         ref='target'
         v-bind="isAnimated ? cleanAttrs(slotAttrs) : slotAttrs"
         :src="currentSrc"
         :alt="props.alt"
         decoding="async"
         draggable="false"
         :class="props.class"
         @load="handleLoad"
         @error="handleError"
    >

    <img v-else-if="isLoading"
         :src="placeholder"
         v-bind="cleanAttrs(slotAttrs)"
         draggable="false"
         :style="calcSizes({ width: slotAttrs.width, height: slotAttrs.height })"
         alt="Skeleton"
    >
    <img v-else
         :src="placeholder"
         v-bind="cleanAttrs(slotAttrs)"
         draggable="false"
         :style="[calcSizes({ width: slotAttrs.width, height: slotAttrs.height }), {objectFit: 'contain'}]"
         alt="Error Placeholder"
         :class="props.class"
    >
  </NuxtImg>
</template>
